import React, { useState, useRef, useEffect, forwardRef } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Text,
  Flex,
  Box,
  Tooltip,
} from "@chakra-ui/react";
import {
  textSecondary,
  buttonBlue,
  buttonBlueText,
  buttonBlueDark,
  buttonGreen,
  buttonGreenText,
  buttonGreenDark,
  buttonRed,
  buttonRedText,
  buttonRedDark,
  buttonPurple,
  buttonPurpleDark,
  buttonYellow,
  buttonYellowDark,
  white,
  backgroundLight,
  newBorder,
  buttonGreenVividDark,
  buttonAltGrey,
  buttonAltPrimary,
  buttonAltGreen,
  buttonAltYellow,
  buttonAltSuccess,
  buttonAltRed,
  text,
  textPrimary,
  secondary,
  greenVivid,
  buttonRedNew,
  color1,
  color2,
  color3,
  color6,
  color5,
  color8,
  color4,
  primaryAlt,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import {
  FaRegCircleDot,
  FaRegCircle,
  FaCircleHalfStroke,
  FaCircleXmark,
  FaClock,
  FaCircleCheck,
  FaCircleExclamation,
  FaCaretDown,
  FaRegCircleCheck,
} from "react-icons/fa6";
import { shade } from "polished";

const StatusButtonNew = forwardRef(
  (
    {
      currentStatus,
      onUpdateStatus,
      options,
      isUnclickable,
      isBig,
      isSmall,
      isRounded,
      onChangeStatus,
      isServiceButton,
      isDisabled,
      isToggle,
      toggleHandler,
      isLong,
      isTooltip,
      tooltipLabel,
      isNoLabel,
      w,
      p,
      maxW,
      minW,
      size,
      h,
      fontSize,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const btnRef = useRef();
    const { t } = useTranslation();
    const translate = (key) => t(key);

    const handleStatusChange = (status) => {
      onUpdateStatus(status);
      // console.log("handleStatusChange", status);
      setIsOpen(false);
    };

    const handleToggleClick = () => {
      // console.log("handleToggleClick");
      toggleHandler();
      setIsOpen(!isOpen);
    };

    const handleClickOutside = (event) => {
      if (btnRef.current && !btnRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (options === "user") {
      options = [
        {
          value: "new",
          label: translate("status.patientNew"),
          icon: FaRegCircleDot,
          bg: color8,
          bgDark: buttonBlueDark,
          color: textPrimary,
          borderColor: buttonBlue,
          bgHover: "#b6cce1",
        },
        {
          value: "inactive",
          label: translate("status.patientInactive"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: text,
          borderColor: buttonAltGrey,
          bgHover: "#ddd",
        },

        {
          value: "active",
          label: translate("status.patientActive"),
          icon: FaCircleCheck,
          bg: buttonAltGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonAltGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "consulting",
          label: translate("status.patientConsulting"),
          icon: FaClock,
          bg: color1,
          bgDark: buttonBlueDark,
          color: white,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "planningTreatment",
          label: translate("status.patientPlanningTreatment"),
          icon: FaClock,
          bg: color2,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "approvedTreatment",
          label: translate("status.patientApprovedTreatment"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonAltGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "ongoingTreatment",
          label: translate("status.patientOngoingTreatment"),
          icon: FaCircleHalfStroke,
          bg: color4,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonAltYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelledTreatment",
          label: translate("status.patientCancelledTreatment"),
          icon: FaCircleXmark,
          bg: buttonAltRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "completedTreatment",
          label: translate("status.patientCompletedTreatment"),
          icon: FaCircleCheck,
          bg: buttonAltSuccess,
          bgDark: buttonGreenVividDark,
          color: textPrimary,
          borderColor: buttonAltSuccess,
          bgHover: "#dda3a3",
        },
      ];
    } else if (options === "patient") {
      options = [
        {
          value: "new",
          label: translate("patientStatus.new"),
          icon: FaRegCircleDot,
          bg: color8,
          bgDark: buttonBlueDark,
          color: textPrimary,
          borderColor: buttonBlue,
          bgHover: "#b6cce1",
        },
        {
          value: "inactive",
          label: translate("patientStatus.inactive"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: text,
          borderColor: buttonAltGrey,
          bgHover: "#ddd",
        },

        {
          value: "planningTreatment",
          label: translate("patientStatus.planningTreatment"),
          icon: FaClock,
          bg: color2,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "approvedTreatment",
          label: translate("patientStatus.approvedTreatment"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonAltGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "ongoingTreatment",
          label: translate("patientStatus.ongoingTreatment"),
          icon: FaCircleHalfStroke,
          bg: color4,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonAltYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelledTreatment",
          label: translate("patientStatus.cancelledTreatment"),
          icon: FaCircleXmark,
          bg: color6,
          bgDark: buttonRedDark,
          color: white,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "completedTreatment",
          label: translate("patientStatus.completedTreatment"),
          icon: FaCircleCheck,
          bg: buttonAltSuccess,
          bgDark: buttonGreenVividDark,
          color: textPrimary,
          borderColor: buttonAltSuccess,
          bgHover: "#dda3a3",
        },
      ];
    } else if (options === "employee") {
      options = [
        {
          value: "inactive",
          label: translate("employeeStatus.inactive"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: text,
          borderColor: buttonAltGrey,
          bgHover: "#ddd",
        },

        {
          value: "active",
          label: translate("employeeStatus.active"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonAltGreen,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "createPatient") {
      options = [
        {
          value: "new",
          label: translate("status.patientNew"),
          icon: FaRegCircleDot,
          bg: color8,
          bgDark: buttonBlueDark,
          color: textPrimary,
          borderColor: buttonBlue,
          bgHover: "#b6cce1",
        },
        {
          value: "inactive",
          label: translate("status.patientInactive"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: text,
          borderColor: buttonAltGrey,
          bgHover: "#ddd",
        },
      ];
    } else if (options === "createEmployee") {
      options = [
        {
          value: "active",
          label: translate("status.active"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },

        {
          value: "inactive",
          label: translate("status.inactive"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: text,
          borderColor: buttonAltGrey,
          bgHover: "#ddd",
        },
      ];
    } else if (options === "appointment") {
      options = [
        {
          value: "scheduled",
          label: translate("status.appointmentScheduled"),
          icon: FaRegCircle,
          bg: buttonBlue,
          bgDark: buttonBlueDark,
          color: buttonBlueText,
          borderColor: newBorder,
          bgHover: "#ddd",
        },

        {
          value: "confirmed",
          label: translate("status.appointmentConfirmed"),
          icon: FaCircleCheck,
          bg: buttonGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "completed",
          label: translate("status.appointmentCompleted"),
          icon: FaCircleCheck,
          bg: buttonGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelled",
          label: translate("status.appointmentCancelled"),
          icon: FaCircleXmark,
          bg: buttonRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
      ];
    } else if (options === "service") {
      options = [
        {
          value: "completed",
          label: translate("status.serviceCompleted"),
          icon: FaCircleCheck,
          bg: buttonGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "uncompleted",
          label: translate("status.serviceUncompleted"),
          icon: FaCircleXmark,
          bg: buttonRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: buttonRedDark,
        },
      ];
    } else if (options === "invoice") {
      options = [
        {
          value: "draft",
          label: translate("status.invoiceDraft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "open",
          label: translate("status.invoiceOpen"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "paid",
          label: translate("status.invoicePaid"),
          icon: FaCircleCheck,
          bg: buttonAltSuccess,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "void",
          label: translate("status.invoiceVoid"),
          icon: FaCircleXmark,
          bg: buttonAltRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "uncollectible",
          label: translate("status.invoiceUncollectible"),
          icon: FaCircleXmark,
          bg: buttonAltRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
      ];
    } else if (options === "treatment") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "approved",
          label: translate("status.treatmentPlanApproved"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },

        {
          value: "consulting",
          label: translate("status.treatmentPlanConsulting"),
          icon: FaClock,
          bg: color1,
          bgDark: buttonBlueDark,
          color: white,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "planning",
          label: translate("status.treatmentPlanPlanning"),
          icon: FaClock,
          bg: color2,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "completed",
          label: translate("status.treatmentPlanCompleted"),
          icon: FaCircleCheck,
          bg: color5,
          bgDark: buttonGreenDark,
          color: textPrimary,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelled",
          label: translate("status.treatmentPlanCancelled"),
          icon: FaCircleXmark,
          bg: color6,
          bgDark: buttonRedDark,
          color: white,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "ongoing",
          label: translate("status.ongoing"),
          icon: FaCircleHalfStroke,
          bg: color4,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonAltYellow,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "treatmentPublic") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "approved",
          label: translate("status.treatmentPlanApproved"),
          icon: FaCircleCheck,
          bg: buttonAltGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },

        {
          value: "consulting",
          label: translate("status.waitingForApprovalPublic"),
          icon: FaClock,
          bg: buttonAltPrimary,
          bgDark: buttonBlueDark,
          color: textPrimary,
          borderColor: newBorder,
          bgHover: shade(0.8, buttonAltPrimary),
        },
        {
          value: "planning",
          label: translate("status.waitingForApprovalPublic"),
          icon: FaClock,
          bg: buttonAltPrimary,
          bgDark: buttonYellowDark,
          color: textPrimary,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "completed",
          label: translate("status.treatmentPlanCompleted"),
          icon: FaCircleCheck,
          bg: buttonAltSuccess,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelled",
          label: translate("status.treatmentPlanCancelled"),
          icon: FaCircleXmark,
          bg: buttonAltRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "ongoing",
          label: translate("status.ongoing"),
          icon: FaCircleHalfStroke,
          bg: buttonAltYellow,
          bgDark: buttonYellowDark,
          color: textPrimary,
          borderColor: buttonAltYellow,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "attendees") {
      options = [
        {
          value: "registered",
          label: translate("status.registered"),
          icon: FaRegCircleDot,
          bg: buttonBlue,
          bgDark: buttonBlue,
          color: textPrimary,
          borderColor: buttonBlue,
          bgHover: "#ddd",
        },
        {
          value: "confirmed",
          label: translate("status.confirmed"),
          icon: FaCircleCheck,
          bg: buttonGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "attended",
          label: translate("status.attended"),
          icon: FaRegCircleCheck,
          bg: greenVivid,
          bgDark: greenVivid,
          color: textPrimary,
          borderColor: greenVivid,
          bgHover: greenVivid,
        },

        {
          value: "cancelled",
          label: translate("status.cancelled"),
          icon: FaCircleXmark,
          bg: buttonRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },

        {
          value: "not_attended",
          label: translate("status.notAttended"),
          icon: FaCircleExclamation,
          bg: buttonRedNew,
          bgDark: buttonPurpleDark,
          color: white,
          borderColor: buttonPurple,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "active") {
      options = [
        {
          value: "inactive",
          label: translate("status.inactive"),
          icon: FaRegCircle,
          bg: backgroundLight,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },

        {
          value: "active",
          label: translate("status.active"),
          icon: FaCircleCheck,
          bg: buttonGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "serviceNew") {
      options = [
        {
          value: "inactive",
          label: translate("status.serviceInactive"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: text,
          borderColor: buttonAltGrey,
          bgHover: "#ddd",
        },

        {
          value: "active",
          label: translate("status.serviceActive"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonAltGreen,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "payment") {
      options = [
        {
          value: "unpaid",
          label: translate("status.unpaid"),
          icon: FaCircleXmark,
          bg: buttonAltRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },

        {
          value: "paid",
          label: translate("status.paid"),
          icon: FaCircleCheck,
          bg: buttonAltSuccess,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "course") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },

        {
          value: "upcoming",
          label: translate("status.upcoming"),
          icon: FaClock,
          bg: buttonBlue,
          bgDark: buttonBlueDark,
          color: buttonBlueText,
          borderColor: buttonBlue,
          bgHover: "#b6cce1",
        },
        {
          value: "cancelled",
          label: translate("status.cancelled"),
          icon: FaCircleXmark,
          bg: buttonRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "completed",
          label: translate("status.completed"),
          icon: FaCircleCheck,
          bg: buttonGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
      ];
    } else if (options === "createTreatmentPlan-planning") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "planning",
          label: translate("status.treatmentPlanPlanning"),
          icon: FaClock,
          bg: color2,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "approved",
          label: translate("status.treatmentPlanApproved"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelled",
          label: translate("status.treatmentPlanCancelled"),
          icon: FaCircleXmark,
          bg: color6,
          bgDark: buttonRedDark,
          color: white,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
      ];
    } else {
      // You can leave this part as it is
    }

    const currentStatusOption = options.find(
      (option) => option.value === currentStatus
    );

    const handleClick = () => {
      if (onChangeStatus) {
        // Call onChangeStatus when provided
        onUpdateStatus(currentStatus);
      } else {
        setIsOpen(!isOpen);
      }
    };

    // console.log("currentStatusOption", currentStatusOption);

    return (
      <>
        <Tooltip
          p="0.5rem"
          bg={shade(0.6, secondary)}
          borderRadius="1rem"
          label={tooltipLabel}
          isDisabled={!isTooltip}
        >
          <Box ref={ref} p={0} borderRadius="0.75rem">
            <Menu isLazy>
              <MenuButton
                size={size ? size : "md"}
                h={h ? h : "2.5rem"}
                as={Button}
                bg={currentStatusOption ? currentStatusOption.bg : white}
                color={
                  currentStatusOption
                    ? currentStatusOption.color
                    : textSecondary
                }
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{
                  bg: shade(
                    0.3,
                    currentStatusOption ? currentStatusOption.bg : white
                  ),
                  color: isBig ? white : white,
                }}
                w={w ? w : "initial"}
                maxW={maxW ? maxW : "initial"}
                minW={minW ? minW : "initial"}
                p={p ? p : "0.5rem 2rem"}
                borderRadius={
                  isRounded ? "10.75rem" : isBig ? "0.75rem" : "0.75rem"
                }
                onClick={isToggle ? handleToggleClick : handleClick} // Handle click
                _active={{
                  bg: currentStatusOption ? currentStatusOption.bg : white,
                  color: currentStatusOption
                    ? currentStatusOption.color
                    : textSecondary,
                }}
                isDisabled={isDisabled}
              >
                {currentStatusOption ? (
                  <>
                    <Flex gap={0} align="center" justify="center">
                      <Flex align="center">
                        <Icon
                          as={currentStatusOption.icon}
                          mr={isSmall ? "0.25rem" : "0.5rem"}
                          h={isSmall ? "0.65rem" : "0.65rem"}
                          w={isSmall ? "0.65rem" : "0.75rem"}
                          opacity="1"
                          position="relative"
                          // top="-1px"
                        />
                        {isNoLabel ? null : (
                          <Text
                            fontSize={fontSize ? fontSize : "0.875rem"}
                            fontWeight={isBig ? "500" : isSmall ? "600" : "500"}
                            lineHeight="1"
                            // textTransform="capitalize"
                          >
                            {currentStatusOption.label}
                          </Text>
                        )}
                      </Flex>

                      {isUnclickable ? null : (
                        <Icon
                          as={FaCaretDown}
                          ml="0.25rem"
                          h="0.75rem"
                          w="0.75rem"
                        />
                      )}
                    </Flex>
                  </>
                ) : (
                  "Unknown"
                )}
              </MenuButton>

              {isUnclickable ? null : (
                <MenuList p={0} m={0} borderRadius="1rem" overflow="hidden">
                  {options
                    .filter(
                      (option) =>
                        option.value !== currentStatus &&
                        option.value !== "draft"
                    )
                    .map((option) => (
                      <MenuItem
                        bg={option === currentStatusOption ? primaryAlt : white}
                        key={option.value}
                        onClick={() => handleStatusChange(option.value)}
                        color={
                          option === currentStatusOption ? white : textSecondary
                        }
                        py={0}
                        px={6}
                        h="3rem"
                        borderBottom={`1px solid ${newBorder}`}
                        _hover={{
                          bg:
                            option === currentStatusOption
                              ? primaryAlt
                              : backgroundLight,
                          color:
                            option === currentStatusOption
                              ? white
                              : textPrimary,
                        }}
                      >
                        <Flex
                          align="center"
                          justify="center"
                          w="1rem"
                          h="1rem"
                          bg={option.bg}
                          borderRadius="10.25rem"
                          mr="0.5rem"
                          position="relative"
                          top="-1px"
                        >
                          <Icon
                            as={option.icon}
                            color={option.color}
                            // mr="0.5rem"
                            fontSize="0.5rem"
                            style={{
                              pointerEvents: "none",
                            }}
                          />
                        </Flex>

                        <Text
                          fontSize="0.8rem"
                          fontWeight="500"
                          lineHeight="1"
                          style={{
                            pointerEvents: "none",
                          }}
                          // color={textSecondary}
                        >
                          {option.label}
                        </Text>
                      </MenuItem>
                    ))}
                </MenuList>
              )}
            </Menu>
          </Box>
        </Tooltip>
      </>
    );
  }
);

export default StatusButtonNew;
